import { Box, Button, Modal, Text } from '@nimbus-ds/components';
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  createNavigationGuard,
  type DiscardEdits,
  type NavigationGuard,
} from './navigationGuard.model.js';

const NavigationGuardContext = createContext<NavigationGuard | null>(null);

/**
 * Holds back navigation while a screen has unsaved edits.
 *
 * Every route change goes through `Router`'s single `navigate`, and the two in-page section
 * changes (the Quantity Offers list ⇄ its offer editor) go through `request` directly, so this
 * one provider covers the whole admin. A screen opts in by calling `useUnsavedChanges`.
 *
 * The prompt offers "Keep editing" and "Discard changes" — and nothing else. A third
 * "leave anyway" would put the merchant back where they started: away from a screen that still
 * holds work they cannot see.
 */
export function NavigationGuardProvider({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  // The guard owns its own state; this counter exists only to repaint on its transitions.
  // `useReducer`'s dispatch is stable, so the guard is built exactly once.
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const guard = useMemo(() => createNavigationGuard(bump), [bump]);
  const blocked = guard.isBlocked();

  // The last exit this app does not own: the merchant closes the tab, or the Nuvemshop admin
  // navigates the iframe out from under us. Only attached while something is unsaved, so a
  // clean app never makes the browser ask.
  useEffect(() => {
    if (!blocked) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Chrome still requires the legacy assignment; the string itself is never displayed.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [blocked]);

  return (
    <NavigationGuardContext.Provider value={guard}>
      {children}
      <Modal open={guard.isPrompting()} onDismiss={() => guard.cancel()}>
        <Modal.Header title={t('common.unsavedChanges')} />
        <Modal.Body padding="none">
          <Text>{t('common.unsavedPrompt')}</Text>
        </Modal.Body>
        <Modal.Footer>
          <Box display="flex" justifyContent="flex-end" gap="2" width="100%">
            <Button onClick={() => guard.cancel()}>{t('common.keepEditing')}</Button>
            <Button appearance="danger" onClick={() => guard.discardAndContinue()}>
              {t('common.discardChanges')}
            </Button>
          </Box>
        </Modal.Footer>
      </Modal>
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard(): NavigationGuard {
  const guard = useContext(NavigationGuardContext);
  if (!guard) throw new Error('useNavigationGuard must be used within NavigationGuardProvider');
  return guard;
}

/**
 * Register this screen's unsaved state, and how to throw it away.
 *
 * `discard` must leave the screen matching what the server has — it is what runs when the
 * merchant picks "Discard changes", immediately before the navigation they asked for.
 */
export function useUnsavedChanges(dirty: boolean, discard: DiscardEdits): void {
  const guard = useNavigationGuard();
  const id = useId();
  // The screen builds a new `discard` closure every render. Keeping it behind a ref means the
  // guard always calls the current one without re-registering (and repainting) on every render.
  const latest = useRef(discard);
  latest.current = discard;
  useEffect(() => {
    guard.setDirty(id, dirty, () => latest.current());
    return () => guard.release(id);
  }, [guard, id, dirty]);
}
